import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Lock, Eye, Database, Globe, UserCheck } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#C9956C]" />
            <h1 className="text-3xl font-bold text-[var(--theme-text-primary)]">Privacy Policy</h1>
          </div>
          <p className="text-[var(--theme-text-secondary)]">Last updated: February 2026</p>
        </div>

        {/* Privacy Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-[var(--theme-surface)] border border-[var(--theme-border)]">
            <Lock className="w-6 h-6 text-[#059669] mb-2" />
            <h3 className="font-semibold text-[var(--theme-text-primary)] mb-1">Secure Payments</h3>
            <p className="text-sm text-[var(--theme-text-secondary)]">Card details handled by Stripe, never stored on our servers</p>
          </div>
          <div className="p-4 rounded-xl bg-[var(--theme-surface)] border border-[var(--theme-border)]">
            <Eye className="w-6 h-6 text-[#059669] mb-2" />
            <h3 className="font-semibold text-[var(--theme-text-primary)] mb-1">Location Privacy</h3>
            <p className="text-sm text-[var(--theme-text-secondary)]">Exact addresses never shared, only suburb/area names</p>
          </div>
          <div className="p-4 rounded-xl bg-[var(--theme-surface)] border border-[var(--theme-border)]">
            <UserCheck className="w-6 h-6 text-[#059669] mb-2" />
            <h3 className="font-semibold text-[var(--theme-text-primary)] mb-1">Your Control</h3>
            <p className="text-sm text-[var(--theme-text-secondary)]">Delete your data anytime via Profile settings</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-8 text-[var(--theme-text-primary)]">
          
          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-[#C9956C]" />
              1. Information We Collect
            </h2>
            
            <div className="space-y-4 text-[var(--theme-text-secondary)]">
              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Account Information</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Email address (required for account creation)</li>
                  <li>Username and display name</li>
                  <li>Profile photo (optional)</li>
                  <li>Phone number (optional, for SMS notifications)</li>
                  <li>Password (securely hashed, never stored in plain text)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Location Data</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Suburb/area for item location display</li>
                  <li>Full address (stored securely, used only for distance calculations)</li>
                  <li>We NEVER share your exact address with other users</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Listing Data</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Item titles, descriptions, categories, and conditions</li>
                  <li>Photos you upload</li>
                  <li>Price ranges and valuations</li>
                  <li>AI-generated item analysis (keywords, categories)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Transaction Data</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Match history and messaging records</li>
                  <li>Payment amounts and dates (via Stripe)</li>
                  <li>Reviews and ratings you give or receive</li>
                  <li>Trust Score calculations</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Usage Data</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Login timestamps and activity patterns</li>
                  <li>Device information and browser type</li>
                  <li>Pages visited and features used</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#C9956C]" />
              2. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li><strong className="text-[var(--theme-text-primary)]">Platform Operations:</strong> Matching buyers with sellers, processing transactions, enabling communication</li>
              <li><strong className="text-[var(--theme-text-primary)]">Trust &amp; Safety:</strong> Calculating Trust Scores, detecting fraud, enforcing Terms of Service</li>
              <li><strong className="text-[var(--theme-text-primary)]">Communications:</strong> Sending notifications about matches, messages, and transactions</li>
              <li><strong className="text-[var(--theme-text-primary)]">Improvements:</strong> Analysing usage patterns to improve the Platform</li>
              <li><strong className="text-[var(--theme-text-primary)]">Legal Compliance:</strong> Responding to legal requests and preventing illegal activity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">3. Information Sharing</h2>
            
            <div className="space-y-4 text-[var(--theme-text-secondary)]">
              <div className="p-4 rounded-xl bg-[#059669]/5 border border-[#059669]/20">
                <h3 className="font-semibold text-[#059669] mb-2">What We Share with Other Users</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Username and profile photo</li>
                  <li>Suburb/area name (NOT exact address)</li>
                  <li>Your listings (titles, descriptions, photos)</li>
                  <li>Trust Score and public reviews</li>
                  <li>Messages you send to them</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-[#DC2626]/5 border border-[#DC2626]/20">
                <h3 className="font-semibold text-[#DC2626] mb-2">What We NEVER Share with Other Users</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Your exact address or GPS coordinates</li>
                  <li>Your email address or phone number</li>
                  <li>Your payment card details</li>
                  <li>Your password</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Third-Party Service Providers</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Stripe:</strong> Payment processing (PCI-DSS compliant)</li>
                  <li><strong>Google:</strong> Maps/Places API for location services</li>
                  <li><strong>Google Gemini:</strong> AI-powered item analysis (images processed securely)</li>
                  <li><strong>Resend:</strong> Email delivery service</li>
                  <li><strong>Firebase:</strong> Authentication services</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">4. Data Security</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              We implement industry-standard security measures:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li>HTTPS encryption for all data transmission</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>JWT tokens for authentication with expiration</li>
              <li>Regular security audits and updates</li>
              <li>Access controls limiting employee data access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">5. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li><strong className="text-[var(--theme-text-primary)]">Active Accounts:</strong> Data retained while account is active</li>
              <li><strong className="text-[var(--theme-text-primary)]">Inactive Inventory:</strong> Hidden after 30 days of inactivity, restored on login</li>
              <li><strong className="text-[var(--theme-text-primary)]">Deleted Accounts:</strong> Personal data removed within 30 days, transaction records retained for legal/tax purposes</li>
              <li><strong className="text-[var(--theme-text-primary)]">Messages:</strong> Retained for dispute resolution; deleted with account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">6. Your Rights (Australian Privacy Act)</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              Under Australian Privacy Principles (APPs), you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li><strong className="text-[var(--theme-text-primary)]">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-[var(--theme-text-primary)]">Correction:</strong> Update or correct inaccurate information</li>
              <li><strong className="text-[var(--theme-text-primary)]">Deletion:</strong> Request deletion of your account and data</li>
              <li><strong className="text-[var(--theme-text-primary)]">Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong className="text-[var(--theme-text-primary)]">Complaint:</strong> Lodge a complaint with the OAIC if unsatisfied</li>
            </ul>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@wantd.au" className="text-[#C9956C] hover:underline">privacy@wantd.au</a> or 
              use the data export/deletion options in your Profile settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">7. Cookies and Tracking</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              We use essential cookies for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--theme-text-secondary)]">
              <li>Authentication (keeping you logged in)</li>
              <li>Theme preferences (light/dark mode)</li>
              <li>Recently viewed items</li>
            </ul>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mt-4">
              We do not use third-party advertising cookies or sell data to advertisers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">8. Children&apos;s Privacy</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              WANTD is not intended for users under 18 years of age. We do not knowingly collect personal 
              information from children. If you believe a child has provided us with personal information, 
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">9. International Data</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              WANTD primarily operates in Australia. Data may be processed by third-party services in other 
              countries (e.g., Stripe in the USA). We ensure these providers maintain appropriate data 
              protection standards.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">10. Changes to This Policy</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed">
              We may update this Privacy Policy periodically. Material changes will be communicated via email 
              or Platform notification. The &quot;Last updated&quot; date at the top indicates the most recent revision.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-4">11. Contact Us</h2>
            <p className="text-[var(--theme-text-secondary)] leading-relaxed mb-4">
              For privacy-related questions or to exercise your data rights:
            </p>
            <div className="p-4 rounded-xl bg-[var(--theme-surface)] border border-[var(--theme-border)]">
              <div className="flex items-center gap-2 text-[var(--theme-text-primary)] mb-2">
                <Mail className="w-5 h-5 text-[#C9956C]" />
                <a href="mailto:privacy@wantd.au" className="text-[#C9956C] hover:underline">privacy@wantd.au</a>
              </div>
              <p className="text-sm text-[var(--theme-text-secondary)]">
                WANTD Pty Ltd<br />
                Melbourne, Victoria, Australia
              </p>
              <p className="text-sm text-[var(--theme-text-secondary)] mt-2">
                <strong>Office of the Australian Information Commissioner (OAIC):</strong><br />
                <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#C9956C] hover:underline">www.oaic.gov.au</a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-[var(--theme-border)]">
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/terms" className="text-[#C9956C] hover:underline">Terms of Service</Link>
            <Link to="/about-us" className="text-[#C9956C] hover:underline">About Us</Link>
            <Link to="/" className="text-[#C9956C] hover:underline">Back to WANTD</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
